import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useNotifications } from '@/hooks/use-notifications.ts';
import { usePostSubscribePushNotifications } from '@/features/notifications/hooks/mutations/use-post-subscribe-push-notifications.ts';
import { usePushNotificationStore } from '@/services/store/use-push-notification-store.ts';
import { usePostUpdateStatusPushNotifications } from '@/features/notifications/hooks/mutations/use-post-update-status-push-notification.ts';

interface PushNotificationsContextType {
  isSubscribed: boolean;
  isLoading: boolean;
  status: StatusMessage | null;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  clearStatus: () => void;
  arePushNotificationsAllowed: boolean | undefined;
  setArePushNotificationsAllowed: (unixDate: boolean) => void;
}

interface StatusMessage {
  title: string;
  description: string;
  type: 'success' | 'error';
  permission?: boolean;
  subscription?: boolean;
}

interface NotificationsProviderProps {
  children: ReactNode;
}

const PushNotificationsContext = createContext<PushNotificationsContextType | undefined>(
  undefined
);

function hasPassed7Days(specificDate: number): boolean {
  const currentDate = new Date();
  const dateToCompare = new Date(specificDate);

  const timeDifference = currentDate.getTime() - dateToCompare.getTime();
  const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

  return daysDifference >= 7;
}

export const PushNotificationsProvider: React.FC<NotificationsProviderProps> = ({
  children,
}) => {
  const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;

  const {
    arePushNotificationsAllowed,
    lastSubscriptionStatusSentAt,
    setLastSubscriptionStatusSentAt,
    setArePushNotificationsAllowed,
  } = usePushNotificationStore();

  const { mutate: postSubscribePushNotifications } = usePostSubscribePushNotifications({
    onSuccess: () => setLastSubscriptionStatusSentAt(new Date().getTime()),
  });

  const { mutate: postUpdatePushNotificationsStatus } = usePostUpdateStatusPushNotifications({
    onSuccess: () => setLastSubscriptionStatusSentAt(new Date().getTime()),
  });

  const notifications = useNotifications({
    vapidPublicKey,
    onSubscriptionChange: async (subscription) => {
      if (subscription) {
        const subscriptionJson = subscription.toJSON();

        setArePushNotificationsAllowed(true);
        postSubscribePushNotifications({
          endpoint: subscription.endpoint,
          expirationTime: subscription.expirationTime,
          p256dhKey: subscriptionJson.keys?.p256dh,
          authKey: subscriptionJson.keys?.auth,
        });
      }
    },
  });

  useEffect(() => {
    if (!notifications.subscriptionDetails?.authKey) return;

    if (!lastSubscriptionStatusSentAt) {
      setLastSubscriptionStatusSentAt(new Date().getTime());
      return;
    }

    if (hasPassed7Days(lastSubscriptionStatusSentAt)) {
      postUpdatePushNotificationsStatus({
        authKey: notifications.subscriptionDetails?.authKey,
      });
    }
  }, [notifications.subscriptionDetails?.authKey]);

  return (
    <PushNotificationsContext.Provider
      value={{ ...notifications, arePushNotificationsAllowed, setArePushNotificationsAllowed }}
    >
      {children}
    </PushNotificationsContext.Provider>
  );
};

export const usePushNotificationsContext = (): PushNotificationsContextType => {
  const context = useContext(PushNotificationsContext);
  if (context === undefined) {
    throw new Error('usePushNotificationsContext must be used within a NotificationsProvider');
  }
  return context;
};
